import axios from 'axios';
import { useEffect, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from './Footer';
import Header from './Header';

const Register = () => {
  const initialFormState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    country: '',
    address: '',
    city: '',
    borough: '',
    state: '',
    zip: '',
    paymentMethod: '',
    paymentFrequency: '',
    password: '',
    confirmPassword: '',
    userType: 'customer',
    licenseNumber: '',
    licensePlate: '',
    vehicleBrand: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleColor: '',
    paymentContact: '',
    ach_account_name: '',
    ach_account_number: '',
    ach_routing_number: '',
    ach_account_type: '',
    stripe_account_id: '',
    rc_id: '',
    tenant_id: ''
  };

  const [form, setForm] = useState(initialFormState);

  const [alert, setAlert] = useState({ message: '', type: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [centers, setCenters] = useState([]);
  const [tenant, setTenant] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);

  /* 🔹 NEW: Tenant Service Area Data */
  const [tenantServiceAreas, setTenantServiceAreas] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableBoroughs, setAvailableBoroughs] = useState([]);
  const [availableZips, setAvailableZips] = useState([]);
  const [noServiceArea, setNoServiceArea] = useState(false);

  /* ================= TENANTS ================= */
  useEffect(() => {
    const fetchtenants = async () => {
      try {
        const res = await axios.get("/auth/tenants-dropdown");
        setTenant(res.data);
      } catch (err) {
        console.error("Failed to load tenants", err);
      }
    };
    fetchtenants();
  }, []);

  /* ================= SERVICE AREAS BY TENANT ================= */
  useEffect(() => {
    if (!form.tenant_id) {
      setTenantServiceAreas([]);
      setAvailableStates([]);
      setAvailableBoroughs([]);
      setAvailableZips([]);
      setNoServiceArea(false);
      return;
    }

    const fetchServiceAreas = async () => {
      try {
        const res = await axios.get(
          `/auth/tenant/service-area/${form.tenant_id}`
        );

        if (!res.data || res.data.length === 0) {
          // 🚫 No service area
          setNoServiceArea(true);
          setTenantServiceAreas([]);
          setAvailableStates([]);
          setAvailableBoroughs([]);
          setAvailableZips([]);
          return;
        }

        // ✅ Service areas exist
        setNoServiceArea(false);
        setTenantServiceAreas(res.data);

        const states = [...new Set(res.data.map(a => a.state))];
        setAvailableStates(states);
      } catch (err) {
        console.error("Failed to load service areas", err);
        setNoServiceArea(true);
      }
    };

    fetchServiceAreas();
  }, [form.tenant_id]);


  /* ================= STATE → BOROUGH ================= */
  useEffect(() => {
    if (!form.state) {
      setAvailableBoroughs([]);
      setAvailableZips([]);
      return;
    }

    const boroughs = tenantServiceAreas
      .filter(a => a.state === form.state)
      .map(a => a.borough);

    setAvailableBoroughs([...new Set(boroughs)]);
  }, [form.state, tenantServiceAreas]);

  /* ================= BOROUGH → ZIP ================= */
  useEffect(() => {
    if (!form.borough) {
      setAvailableZips([]);
      return;
    }

    const zips = tenantServiceAreas
      .filter(
        a => a.state === form.state && a.borough === form.borough
      )
      .map(a => a.zipcode);

    setAvailableZips([...new Set(zips)]);
  }, [form.borough, form.state, tenantServiceAreas]);

  /* ================= REDEMPTION CENTERS ================= */
  useEffect(() => {
    if (!form.tenant_id) {
      setCenters([]);
      setForm(prev => ({ ...prev, rc_id: '' }));
      return;
    }

    const fetchCentersByTenant = async () => {
      try {
        setLoadingCenters(true);
        setCenters([]);
        setForm(prev => ({ ...prev, rc_id: '' }));

        const res = await axios.get(
          `/auth/redemption-centers/${form.tenant_id}`
        );

        setCenters(res.data);
      } catch (err) {
        console.error("Failed to load redemption centers", err);
        setCenters([]);
      } finally {
        setLoadingCenters(false);
      }
    };

    fetchCentersByTenant();
  }, [form.tenant_id]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'tenant_id' && { state: '', borough: '', zip: '' }),
      ...(name === 'state' && { borough: '', zip: '' }),
      ...(name === 'borough' && { zip: '' })
    }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (noServiceArea) {
      toast.error("Selected tenant has no active service areas.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setAlert({ message: 'Passwords do not match', type: 'error' });
      return;
    }

    try {
      const response = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        setAlert({ message: data.message, type: 'success' });

        // ✅ RESET FORM
        setForm(initialFormState);
        setCenters([]);
        setAvailableStates([]);
        setAvailableBoroughs([]);
        setAvailableZips([]);
      } else {
        setAlert({ message: data.message || 'Registration failed', type: 'error' });
      }
    } catch {
      setAlert({ message: 'Server error', type: 'error' });
    }
  };


  return (
    <div>
      <Header />
      <div className="min-h-screen items-center justify-center">
        <div className="max-w-2xl mx-auto my-8 p-6 sm:p-6 md:p-8 bg-white rounded-lg shadow-[0_0_10px_0_#b2b2b2]">
          <h1 className="text-center text-2xl font-semibold mb-4">
            Please <span className="text-[#9cc026]">Register</span> To Receive Your{' '}
            <span className="text-[#9cc026]">Free Welcome Kit</span>
          </h1>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* User Type */}
            <div>
              <label className="block text-sm font-medium mb-1">I am registering as a:</label>
              <div className="flex items-center gap-6">
                <label className="block text-sm font-medium mb-1">
                  <input type="radio" name="userType" value="customer"
                    checked={form.userType === 'customer'} onChange={handleChange} />
                  Customer
                </label>
                <label className="block text-sm font-medium mb-1">
                  <input type="radio" name="userType" value="driver"
                    checked={form.userType === 'driver'} onChange={handleChange} />
                  Driver
                </label>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1">Full Name <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input name="firstName" value={form.firstName} onChange={handleChange}
                  type="text" placeholder="First Name" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="lastName" value={form.lastName} onChange={handleChange}
                  type="text" placeholder="Last Name" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
            </div>

            {/* Contact */}
            <div>
              <label className="block text-sm font-medium mb-1">Contact Details <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input name="email" value={form.email} onChange={handleChange}
                  type="email" placeholder="Email" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="phone" value={form.phone} onChange={handleChange}
                  type="tel" placeholder="Phone Number" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
            </div>

            {/* DOB & Country */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date of Birth</label>
                <input name="dob" value={form.dob} onChange={handleChange}
                  type="date" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <select name="country" value={form.country} onChange={handleChange} required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm">
                  <option value="">Select Country</option>
                  <option value="Romania">Romania</option>
                  <option value="USA">USA</option>
                  <option value="India">India</option>
                </select>
              </div>
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Street Address</label>
                <input name="address" value={form.address} onChange={handleChange}
                  type="text" placeholder="Street Address" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input name="city" value={form.city} onChange={handleChange}
                  type="text" placeholder="City" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select a tenant</label>
                <select name="tenant_id" value={form.tenant_id} onChange={handleChange} required className="w-full p-2 border rounded">
                  <option value="">Select Tenant</option>
                  {tenant.map(t => (
                    <option key={t.id} value={t.id}>{t.tenant_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Select a Redemption center</label>
                <select name="rc_id" value={form.rc_id || ""} onChange={handleChange}
                  required disabled={!form.tenant_id || centers.length === 0}
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm disabled:bg-gray-200">
                  <option value="">
                    {loadingCenters ? "Loading redemption centers..."
                      : centers.length === 0
                        ? "No redemption centers available" : "Select Redemption Center"}
                  </option>
                  {centers.map((center) => (
                    <option key={center.rc_id} value={center.rc_id}> {center.center_name} </option>
                  ))}
                </select>
              </div>
            </div>

            {noServiceArea && (
              <div className="mt-3 p-2 rounded-md bg-red-50 border border-red-300 text-red-700 text-xs font-medium">
                🚫 This tenant has no active service areas. Please choose a different tenant or try again later.
              </div>
            )}

            {/* Borough / State / ZIP */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <select name="state" value={form.state} onChange={handleChange} required disabled={!availableStates.length} className="w-full p-2 border rounded disabled:bg-gray-200">
                  <option value="">Select State</option>
                  {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
                </select></div>
              <div>
                <label className="block text-sm font-medium mb-1">Borough</label>
                <select name="borough" value={form.borough} onChange={handleChange} required disabled={!availableBoroughs.length} className="w-full p-2 border rounded disabled:bg-gray-200">
                  <option value="">Select Borough</option>
                  {availableBoroughs.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zip</label>
                <select name="zip" value={form.zip} onChange={handleChange} required disabled={!availableZips.length} className="w-full p-2 border rounded disabled:bg-gray-200">
                  <option value="">Select ZIP</option>
                  {availableZips.map(z => <option key={z} value={z}>{z}</option>)}
                </select></div>
            </div>

            {/* Driver Details */}
            {form.userType === 'driver' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input name="licenseNumber" value={form.licenseNumber} onChange={handleChange}
                  placeholder="Driver License Number" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="licensePlate" value={form.licensePlate} onChange={handleChange}
                  placeholder="License Plate Number" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="vehicleBrand" value={form.vehicleBrand} onChange={handleChange}
                  placeholder="Vehicle Brand" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="vehicleModel" value={form.vehicleModel} onChange={handleChange}
                  placeholder="Vehicle Model" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="vehicleYear" value={form.vehicleYear} onChange={handleChange}
                  placeholder="Vehicle Year" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                <input name="vehicleColor" value={form.vehicleColor} onChange={handleChange}
                  placeholder="Vehicle Color" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
            )}

            {/* Payment */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">How do you want to be paid?</label>
                <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange} required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm">
                  <option value="">Select Payment Method</option>
                  <option value="Cash">Cash</option>
                  <option value="ACH">ACH "Electronic Payment"</option>
                  <option value="Ev-voucher">EV charging voucher"</option>
                  <option value="Fuel-Voucher">Fuel Voucher</option>
                  <option value="Stock-credit">Stock-credit</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Check by Mail">Check by Mail</option>
                </select>
                <p className="text-sm text-gray-600 mt-1">
                  Service Fees: <strong>10%</strong> per payment <em>(PayPal / Stripe fee)</em>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Frequency</label>
                <div className="flex items-center gap-6">
                  <label className="block text-sm font-medium mb-1">
                    <input type="radio" name="paymentFrequency" value="Weekly" checked={form.paymentFrequency === 'Weekly'}
                      onChange={handleChange} className="form-radio text-green-600" required />
                    <span className="ml-2">Weekly</span>
                  </label>
                  <label className="block text-sm font-medium mb-1">
                    <input type="radio" name="paymentFrequency" value="Daily"
                      checked={form.paymentFrequency === 'Daily'} onChange={handleChange} className="form-radio text-green-600" />
                    <span className="ml-2">Daily</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Extra Payment Info for Driver */}
            {form.paymentMethod === 'PayPal' && (
              <div>
                <label className="block text-sm font-medium mb-1">PayPal Email</label>
                <input type="email" name="paymentContact" value={form.paymentContact}
                  onChange={handleChange} placeholder="PayPal Email" required
                  className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
              </div>
            )}

            {form.paymentMethod === 'ACH' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">ACH Account Name</label>
                    <input type="text" name="ach_account_name" value={form.ach_account_name} required
                      onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ACH Account Number</label>
                    <input type="text" name="ach_account_number" value={form.ach_account_number} required
                      onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">ACH Routing Number</label>
                    <input type="text" name="ach_routing_number" value={form.ach_routing_number}
                      onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ACH Account Type</label>
                    <select name="ach_account_type" value={form.ach_account_type} onChange={handleChange}
                      className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required>
                      <option value="">Select Account Type</option>
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stripe Account ID</label>
                  <input type="text" name="stripe_account_id" required
                    value={form.stripe_account_id} onChange={handleChange}
                    className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                </div>
              </>
            )}

            {/* Password & Confirm Password in same row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input name="password" value={form.password} onChange={handleChange}
                    type={showPassword ? 'text' : 'password'} required
                    className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600">
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium mb-1"> Confirm Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input name="confirmPassword" value={form.confirmPassword}
                    onChange={handleChange} type={showConfirmPassword ? 'text' : 'password'} required
                    className={`w-full p-2.5 rounded-md bg-green-50 border text-sm ${form.confirmPassword && form.password !== form.confirmPassword
                      ? 'border-red-500'
                      : 'border-gray-300'
                      }`} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600">
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                )}
              </div>
            </div>

            {/* Terms */}
            <div>
              <label className="inline-flex items-start gap-2">
                <input type="checkbox" name="agree" required className="mt-1" /> <span>I agree to the Terms and Conditions.</span>
              </label>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap justify-end gap-4">
              <button type="button"
                onClick={() => {
                  setForm(initialFormState); setCenters([]);
                  setAvailableStates([]); setAvailableBoroughs([]);
                  setAvailableZips([]); setAlert({ message: '', type: '' });
                }}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black font-semibold rounded-3xl">
                RESET
              </button>

              <button type="submit" disabled={centers.length === 0} className={`px-6 py-3 font-semibold rounded-3xl text-white
    ${centers.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#54BE73] hover:bg-green-700'}
  `}
              > COMPLETE MY REGISTRATION </button>
            </div>

            {alert.message && (
              <div className={`mt-6 p-4 text-center font-semibold text-white rounded-md ${alert.type === 'success' ? 'bg-green-600' : 'bg-red-600'
                }`}>
                {alert.message}
              </div>
            )}
          </form>
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Register;
